import React, { useState, useRef, useEffect } from "react";
import { Search, Loader2, X } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { brcoinService } from "../services/brcoinService";
import { CarteiraMilitarModal } from "./modals/CarteiraMilitarModal";
import { AniversariantesSemana } from "./AniversariantesSemana";

interface MilitarData {
  nick: string;
  patente: string;
  sigla?: string;
  ultima_promocao: string;
  promovido_por: string;
  status: string;
}

interface DashboardHeaderProps {
  isCollapsed: boolean;
}

export function DashboardHeader({ isCollapsed }: DashboardHeaderProps) {
  const [searchNick, setSearchNick] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [militarData, setMilitarData] = useState<MilitarData | null>(null);
  const [brcoins, setBrcoins] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    brcoinService.getSaldo()
      .then((data) => setBrcoins(data.brcoins))
      .catch(() => setBrcoins(null));
  }, []);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const nick = searchNick.trim();
    if (!nick) return;

    setLoading(true);
    try {
      const response = await api.get(`/militares/findByNick?nick=${encodeURIComponent(nick)}`);
      setMilitarData(response.data);
      setModalOpen(true);
    } catch (error: any) {
      const message = error.response?.data?.message || "Militar não encontrado";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchNick("");
    inputRef.current?.focus();
  };

  return (
    <>
      <div
        className={`fixed top-0 right-0 z-30 h-14 flex items-center justify-center px-4 gap-3 transition-all duration-300 ease-in-out left-0 ${
          isCollapsed ? "lg:left-20" : "lg:left-72"
        }`}
        style={{
          background: "oklch(var(--background))",
          borderBottom: "1px solid oklch(var(--border))",
          backdropFilter: "blur(8px)",
        }}
      >
        {/* Espaço para o botão hamburguer no mobile */}
        <div className="w-10 shrink-0 lg:hidden" />

        {/* Barra de pesquisa de militar */}
        <form
          onSubmit={handleSearch}
          className="flex items-center gap-2 flex-1 lg:flex-none"
        >
          <div className="relative flex items-center flex-1 lg:flex-none">
            <Search
              className="absolute left-3 w-4 h-4 pointer-events-none"
              style={{ color: "oklch(var(--muted-foreground))" }}
            />
            <input
              ref={inputRef}
              type="text"
              value={searchNick}
              onChange={(e) => setSearchNick(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Pesquisar militar..."
              className="pl-9 pr-8 py-1.5 text-sm rounded-lg outline-none transition-all w-full lg:w-48 lg:focus:w-64"
              style={{
                background: "oklch(var(--muted) / 0.5)",
                border: "1px solid oklch(var(--border))",
                color: "oklch(var(--foreground))",
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = "0 0 0 2px oklch(0.45 0.15 142 / 0.4)";
                e.target.style.borderColor = "oklch(0.45 0.15 142)";
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = "";
                e.target.style.borderColor = "oklch(var(--border))";
              }}
            />
            {searchNick && !loading && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 p-0.5 rounded hover:bg-muted transition-colors"
                style={{ color: "oklch(var(--muted-foreground))" }}
              >
                <X className="w-3 h-3" />
              </button>
            )}
            {loading && (
              <Loader2
                className="absolute right-2.5 w-3.5 h-3.5 animate-spin pointer-events-none"
                style={{ color: "oklch(0.45 0.15 142)" }}
              />
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !searchNick.trim()}
            className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            style={{
              background: "oklch(0.45 0.15 142)",
              color: "#fff",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.40 0.15 142)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.45 0.15 142)";
            }}
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Search className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">Buscar</span>
          </button>
        </form>

        {/* Saldo BRCoins */}
        {brcoins !== null && (
          <button
            onClick={() => navigate("/loja/extrato")}
            className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-semibold rounded-lg transition-all shrink-0"
            style={{
              background: "oklch(0.85 0.12 85 / 0.15)",
              border: "1px solid oklch(0.75 0.15 85 / 0.4)",
              color: "oklch(0.55 0.15 85)",
            }}
            title="Ver extrato de BRCoins"
          >
            <span style={{ fontSize: "14px" }}>₿</span>
            <span className="hidden sm:inline">{brcoins.toLocaleString("pt-BR")}</span>
            <span className="sm:hidden">{brcoins >= 1000 ? `${(brcoins / 1000).toFixed(1)}k` : brcoins}</span>
          </button>
        )}

        {/* Aniversariantes da Semana */}
        <AniversariantesSemana />
      </div>

      {/* Modal Carteira Militar */}
      <CarteiraMilitarModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setMilitarData(null);
        }}
        data={militarData}
      />
    </>
  );
}
